import { useRef } from 'react';

import { useTaskProcessor } from '@eluve/appointment-tasks';
import { Button, ButtonProps, Input, useToast } from '@eluve/components';
import {
  useAppointmentStatus,
  useIsUserAppointmentOwner,
} from '@eluve/frontend-appointment-hooks';
import {
  AppointmentFileTypesLookup,
  AppointmentStatusTypesLookup,
} from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';
import {
  useIsFeatureFlagEnabled,
  useSetAppointmentStartedAt,
} from '@eluve/smart-blocks';
import {
  supportedAudioFormats,
  useIsUploadInProgressForAppointment,
  usePeriodicRemoteFileSyncActor,
  useUserFileSystemStore,
} from '@eluve/user-local-files';
import { isAudioFileChunkFile, makeStandardAudioFileName } from '@eluve/utils';

interface UploadAudioFileButtonProps {
  appointmentId: string;
  buttonSize?: ButtonProps['size'];
}

const supportedFileExtensions = supportedAudioFormats.map(
  (audioFormat) => `.${audioFormat.extension}`,
);

const acceptedFileTypes = ['audio/*', ...supportedFileExtensions].join(',');

export const UploadAudioFileButton: React.FC<UploadAudioFileButtonProps> = ({
  appointmentId,
  buttonSize = 'lg',
}) => {
  const userId = useUserIdFromSession();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logger = useNamedLogger(UploadAudioFileButton.name);
  const analytics = useAnalytics('files');

  const userFileSystem = useUserFileSystemStore(
    (state) => state.userFileSystem,
  );
  const setAppointmentStartedAt = useSetAppointmentStartedAt();
  const appointmentStatus = useAppointmentStatus();
  const isUserAppointmentOwner = useIsUserAppointmentOwner();

  const { send } = usePeriodicRemoteFileSyncActor();
  const hasPendingAppointmentTask =
    useIsUploadInProgressForAppointment(appointmentId);

  const { taskProcessor } = useTaskProcessor();

  const chunkedBatchTranscriptionEnabled = useIsFeatureFlagEnabled(
    'CHUNKED_BATCH_TRANSCRIPTION',
  );

  const isUploadFileButtonDisabled =
    hasPendingAppointmentTask || !isUserAppointmentOwner;

  const handleUploadFileButtonClick = () => {
    // open file dialog
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file: File): Promise<void> => {
    const fileExtension = file.name.split('.').pop();

    analytics.capture('upload_attempt', {
      appointmentId,
      fileName: file.name,
      fileSize: file.size,
      fileExtension,
    });

    const audioFormat = supportedAudioFormats.find(
      (audioFormat) => audioFormat.extension === fileExtension,
    );

    if (!audioFormat) {
      toast.error('Unsupported file format');
      logger.warn('User tried to upload an unsupported file format', {
        fileExtension,
      });
      analytics.capture('upload_failed', {
        appointmentId,
        fileExtension,
        reason: 'unsupported_format',
      });
      return;
    }

    if (userFileSystem.type !== 'available') {
      toast.error('File system not available');
      logger.error(
        'User tried to upload a file but the file system is not available',
        {
          userFileSystem: userFileSystem.type,
        },
      );
      analytics.capture('upload_failed', {
        appointmentId,
        reason: 'file_system_unavailable',
      });
      return;
    }

    const newFileName = makeStandardAudioFileName({
      recordingStartedAt: new Date().toISOString(),
      isChunkingEnabled: chunkedBatchTranscriptionEnabled,
      chunkNum: 0,
      extension: fileExtension,
    });

    try {
      const appointmentDir =
        await userFileSystem.appointmentsDir.getDirectoryHandle(appointmentId, {
          create: true,
        });

      const localFile = await appointmentDir.getFileHandle(newFileName, {
        create: true,
      });

      if ('createWritable' in localFile) {
        const writable = await localFile.createWritable();
        await writable.write(file);
        await writable.close();
      }
    } catch (error) {
      toast.error('Failed to upload file');
      logger.error('User encountered error while uploading file', {
        errorMessage: (error as Error).message,
      });
      analytics.capture('upload_failed', {
        appointmentId,
        fileName: file.name,
        reason: 'opfs_write_error',
      });
      return;
    }

    send({
      type: 'IMMEDIATE_SYNC_AVAILABLE',
      file: {
        appointmentId,
        id: newFileName,
        attemptCount: 0,
        lastUploadAttemptedAt: new Date().toISOString(),
        shouldBeTranscribed: true,
        wasDegraded: false,
        uploadType: isAudioFileChunkFile(newFileName)
          ? AppointmentFileTypesLookup.CHUNKED_AUDIO
          : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
      },
    });

    analytics.capture('upload_success', {
      appointmentId,
      fileName: newFileName,
      fileSize: file.size,
      fileExtension,
    });
    logger.info('File upload completed', {
      appointmentId,
      fileName: newFileName,
    });

    taskProcessor.addTask({
      type: 'TASK.MARK_CHUNK_FINAL',
      appointmentId,
      fileName: newFileName,
      userId,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (appointmentStatus === AppointmentStatusTypesLookup.NOT_STARTED) {
      await setAppointmentStartedAt({
        variables: {
          id: appointmentId,
        },
      });
    }
  };

  return (
    <>
      <Input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
        accept={acceptedFileTypes}
      />

      <Button
        variant="secondary"
        size={buttonSize}
        onClick={handleUploadFileButtonClick}
        disabled={isUploadFileButtonDisabled}
      >
        Upload File
      </Button>
    </>
  );
};
